import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO, getOrganizationSchema, getServiceSchema, getWebSiteSchema } from '../../utils/seo';

// 인증키 설정
const VALID_AUTH_KEY = 'KR2024STUDIO';

const Home = () => {
  const navigate = useNavigate();

  // SEO Setup
  useSEO({
    title: 'KR스튜디오 - 교육 콘텐츠의 기준',
    description:
      '24시간 내 완성되는 교육 영상, 300개 이상 교육기관이 선택한 KR스튜디오. 기획부터 운영까지 원스탑 솔루션을 제공합니다.',
    keywords:
      '교육 콘텐츠, 영상 제작, 이러닝, VOD 제작, 라이브 스트리밍, 스튜디오, 교육 영상, 온라인 강의',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [getWebSiteSchema(), getOrganizationSchema(), getServiceSchema()],
    },
  });

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [authKey, setAuthKey] = useState('');
  const [authKeyError, setAuthKeyError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 컴포넌트 마운트 시 포커스 설정
  useEffect(() => {
    const nameInput = document.getElementById('name') as HTMLInputElement | null;
    if (nameInput) {
      nameInput.focus();
    }
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 숫자만 추출
    const numbersOnly = value.replace(/[^0-9]/g, '');
    
    // 11자리로 제한 (010으로 시작하는 휴대폰 번호)
    const limitedNumbers = numbersOnly.slice(0, 11);
    
    // 자동 포맷팅 적용
    let formattedValue = '';
    if (limitedNumbers.length <= 3) {
      formattedValue = limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      formattedValue = `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`;
    } else {
      formattedValue = `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 7)}-${limitedNumbers.slice(7)}`;
    }
    
    setPhone(formattedValue);
  };

  const handleAuthKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAuthKey(value);
    // 입력 중에는 오류 표시 제거
    if (authKeyError) {
      setAuthKeyError(false);
    }
  };

  const validatePhone = (phoneNumber: string) => {
    // 숫자만 추출하여 검증
    const numbersOnly = phoneNumber.replace(/[^0-9]/g, '');
    
    // 휴대폰 번호 형식 검증 (010, 011, 016, 017, 018, 019로 시작하는 11자리)
    const phoneRegex = /^(010|011|016|017|018|019)[0-9]{8}$/;
    return phoneRegex.test(numbersOnly);
  };

  const validateAuthKey = (key: string) => {
    return key === VALID_AUTH_KEY;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !phone.trim() || !authKey.trim()) {
      alert('모든 정보를 입력해주세요.');
      return;
    }

    // 인증키 검증
    if (!validateAuthKey(authKey)) {
      setAuthKeyError(true);
      alert('스튜디오 관리자에게 문의하세요.\n연락처: 070-8656-8134');
      return;
    }

    // 연락처 유효성 검증
    if (!validatePhone(phone)) {
      alert('올바른 휴대폰 번호를 입력해주세요.\n(예: 010-1234-5678)');
      return;
    }

    setIsLoading(true);

    // 숫자만 추출하여 저장 (일관된 형태로 저장)
    const cleanPhone = phone.replace(/[^0-9]/g, '');

    // 입력된 정보를 세션에 저장하고 예약 페이지로 이동
    try {
      sessionStorage.setItem(
        'userInfo',
        JSON.stringify({ name: name.trim(), phone: cleanPhone })
      );
    } catch (error) {
      console.error('Session storage is not available:', error);
    }

    setTimeout(() => {
      navigate('/studio-booking');
    }, 500);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center px-4"
      style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, gray 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-12">
          <img
            src="https://static.readdy.ai/image/01721397da55af21878e201cee4469af/ca67f813655a88dbcb8d85088ee43926.png"
            alt="KR스튜디오 로고"
            className="h-16 w-auto mx-auto mb-6"
          />
        </div>

        {/* Login Form */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-8 shadow-xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">스튜디오 예약</h2>
            <p className="text-gray-600 text-sm">예약을 위해 기본 정보를 입력해주세요</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                이름 *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={name}
                onChange={handleNameChange}
                autoComplete="name"
                autoFocus
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                placeholder="이름을 입력해주세요"
                required
                style={{ fontSize: '16px' }}
              />
            </div>

            {/* Auth Key Input */}
            <div>
              <label htmlFor="authKey" className="block text-sm font-medium text-gray-700 mb-2">
                예약 인증키 *
              </label>
              <input
                type="text"
                id="authKey"
                name="authKey"
                value={authKey}
                onChange={handleAuthKeyChange}
                className={`w-full px-4 py-3 bg-white border rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 transition-all text-base ${
                  authKeyError 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                }`}
                placeholder="인증키를 입력해주세요"
                required
                style={{ fontSize: '16px' }}
              />
              {authKeyError && (
                <div className="flex items-center mt-2 text-xs text-red-600">
                  <i className="ri-error-warning-line mr-1"></i>
                  <span>잘못된 인증키입니다. 스튜디오 관리자에게 문의하세요.</span>
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                <i className="ri-information-line mr-1"></i>
                인증키는 스튜디오 관리자로부터 받으실 수 있습니다.
              </p>
            </div>

            {/* Phone Input */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                연락처 *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={phone}
                onChange={handlePhoneChange}
                autoComplete="tel"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                placeholder="010-1234-5678"
                required
                maxLength={13}
                style={{ fontSize: '16px' }}
              />
              {phone && !validatePhone(phone) && (
                <div className="text-xs text-red-500 mt-1">올바른 휴대폰 번호를 입력해주세요 (예: 010-1234-5678)</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !name.trim() || !phone.trim() || !authKey.trim()}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  처리중...
                </div>
              ) : (
                '예약 페이지로 이동'
              )}
            </button>
          </form>

          {/* Additional Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center text-gray-600 text-sm">
              <p className="mb-2">문의사항이 있으시면</p>
              <a
                href="tel:070-8656-8134"
                className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
              >
                070-8656-8134
              </a>
              <p className="mt-1">로 연락주세요</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
